import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { CalendarClock, Download, Pause, Play, Repeat, X } from "lucide-react";
import { PageHead, Panel, StatCard } from "@/components/dashboard-shell";
import { formatINR } from "@/data/sample";
import {
  mandates as seedMandates,
  mandateTotals,
  type Mandate,
  type MandateStatus,
} from "@/data/recurring";
import { downloadCsv, printReceipt } from "@/lib/receipt";

export const Route = createFileRoute("/donor/recurring")({ component: RecurringGiving });

function RecurringGiving() {
  const [list, setList] = useState<Mandate[]>(seedMandates);
  const [openId, setOpenId] = useState<string>(seedMandates[0].id);

  const setStatus = (id: string, status: MandateStatus) =>
    setList((prev) =>
      prev.map((m) =>
        m.id === id ? { ...m, status, nextDebit: status === "Active" ? m.nextDebit : status } : m,
      ),
    );

  const activeMonthly = list.filter((m) => m.status === "Active").reduce((s, m) => s + m.amount, 0);
  const contributed = list.reduce((s, m) => s + mandateTotals(m).contributed, 0);
  const receipts = list.reduce((s, m) => s + mandateTotals(m).paidCycles, 0);

  return (
    <>
      <PageHead
        title="Monthly giving"
        subtitle="Manage your mandates, track every debit cycle and download the receipt for each one."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Active monthly" value={`${formatINR(activeMonthly)}/mo`} tone="primary" />
        <StatCard label="Given so far" value={formatINR(contributed)} tone="good" />
        <StatCard label="Mandates" value={String(list.length)} />
        <StatCard label="Receipts issued" value={String(receipts)} tone="warn" />
      </div>

      <div className="mt-6 space-y-4">
        {list.map((m) => {
          const t = mandateTotals(m);
          const open = openId === m.id;
          return (
            <Panel
              key={m.id}
              title={m.programTitle}
              action={
                <button
                  onClick={() => setOpenId(open ? "" : m.id)}
                  className="rounded-full border border-border px-3 py-1 text-xs"
                >
                  {open ? "Hide cycles" : "View cycles"}
                </button>
              }
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-[220px]">
                  <p className="flex items-center gap-1.5 text-sm font-semibold text-[var(--maroon)]">
                    <Repeat className="h-3.5 w-3.5 text-[var(--saffron-deep)]" />{" "}
                    {formatINR(m.amount)}/month
                  </p>
                  <p className="mt-1 text-xs text-foreground/60">
                    {m.orgName} · {m.impact}
                  </p>
                  <p className="mt-2 text-xs text-foreground/70">
                    Mandate {m.id} · {m.method}
                    <br />
                    Ref {m.mandateRef} · started {m.startedOn}
                  </p>
                </div>
                <div className="text-xs text-foreground/60">
                  <p className="flex items-center gap-1">
                    <CalendarClock className="h-3.5 w-3.5" /> Next debit:{" "}
                    <span className="font-medium text-foreground/80">
                      {m.status === "Active" ? m.nextDebit : "—"}
                    </span>
                  </p>
                  <p className="mt-1">
                    {t.paidCycles} cycles paid · {formatINR(t.contributed)} given
                    {t.failed > 0 ? ` · ${t.failed} failed` : ""}
                  </p>
                  <span
                    className={`mt-2 inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                      m.status === "Active"
                        ? "bg-[var(--success)]/12 text-[var(--success)]"
                        : m.status === "Paused"
                          ? "bg-[var(--saffron-deep)]/15 text-[var(--saffron-deep)]"
                          : "bg-foreground/8 text-foreground/60"
                    }`}
                  >
                    {m.status}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {m.status === "Active" ? (
                    <button
                      onClick={() => setStatus(m.id, "Paused")}
                      className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs"
                    >
                      <Pause className="h-3 w-3" /> Pause
                    </button>
                  ) : m.status === "Cancelled" ? null : (
                    <button
                      onClick={() => setStatus(m.id, "Active")}
                      className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs"
                    >
                      <Play className="h-3 w-3" /> Resume
                    </button>
                  )}
                  {m.status !== "Cancelled" && (
                    <button
                      onClick={() => setStatus(m.id, "Cancelled")}
                      className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs text-[var(--danger,#b3261e)]"
                    >
                      <X className="h-3 w-3" /> Cancel
                    </button>
                  )}
                  <button
                    onClick={() =>
                      downloadCsv(`mandate-${m.id}-cycles.csv`, [
                        ["Cycle", "Month", "Debit date", "Amount", "Status", "Receipt"],
                        ...m.cycles.map((c) => [
                          c.cycle,
                          c.month,
                          c.date,
                          c.amount,
                          c.status,
                          c.receiptId ?? "",
                        ]),
                      ])
                    }
                    className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs"
                  >
                    <Download className="h-3 w-3" /> Export cycles
                  </button>
                </div>
              </div>

              {open && (
                <div className="mt-5 overflow-x-auto">
                  <table className="w-full min-w-[620px] text-sm">
                    <thead className="text-xs uppercase tracking-wider text-foreground/70">
                      <tr className="border-b border-border text-left">
                        <th className="py-2">Cycle</th>
                        <th>Month</th>
                        <th>Debit date</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Receipt</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {m.cycles.map((c) => (
                        <tr key={c.cycle}>
                          <td className="py-3">#{c.cycle}</td>
                          <td>{c.month}</td>
                          <td>{c.date}</td>
                          <td className="font-semibold text-[var(--maroon)]">
                            {formatINR(c.amount)}
                          </td>
                          <td>
                            <span className="text-xs">{c.status}</span>
                            {c.note && (
                              <span className="block text-[11px] text-foreground/70">{c.note}</span>
                            )}
                          </td>
                          <td>
                            {c.status === "Paid" && c.receiptId ? (
                              <button
                                onClick={() =>
                                  printReceipt({
                                    id: c.receiptId!,
                                    date: c.date,
                                    donor: "Rahul Shah",
                                    pan: "ABCDE1234F",
                                    amount: c.amount,
                                    purpose: `${m.programTitle} — ${m.impact}`,
                                    beneficiary: m.orgName,
                                    frequency: "Monthly recurring",
                                    cycle: `Cycle #${c.cycle} · ${c.month}`,
                                  })
                                }
                                className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs"
                              >
                                <Download className="h-3 w-3" /> 80G receipt
                              </button>
                            ) : (
                              <span className="text-xs text-foreground/65">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          );
        })}
      </div>
    </>
  );
}
